import React, { useState, useEffect } from 'react';
import {
  Loader2, Play, RotateCcw, CheckCircle2, XCircle,
  ChevronDown, ChevronUp, AlertTriangle, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { getPromptLabDefaults, testFraudPrompt, type TalentResult, type ParsedFraudResult } from '../../services/api';
import { AdminSelect } from './AdminSelect';

// ─── Constants ────────────────────────────────────────────────────────────────

const MODELS = [
  { value: 'gpt-5.4-mini', label: 'GPT-5.4 mini' },
  { value: 'gpt-4.1-2025-04-14', label: 'GPT-4.1' },
  { value: 'gpt-4.1-mini-2025-04-14', label: 'GPT-4.1 mini' },
  { value: 'gpt-4o-2024-11-20', label: 'GPT-4o' },
  { value: 'gpt-4o-mini-2024-07-18', label: 'GPT-4o mini' },
];

const CATEGORIES: { key: string; label: string; weight: number }[] = [
  { key: 'identity',     label: 'Identity & Institutional',  weight: 0.25 },
  { key: 'education',    label: 'Education Claims',           weight: 0.20 },
  { key: 'publications', label: 'Publications & Research',    weight: 0.20 },
  { key: 'employment',   label: 'Employment Timeline',        weight: 0.15 },
  { key: 'awards',       label: 'Awards & Distinctions',      weight: 0.10 },
  { key: 'language',     label: 'Language & Structure',       weight: 0.10 },
];

const RUBRIC: Record<string, Record<number, string>> = {
  identity: {
    0: 'Name consistent, institutions named specifically',
    1: 'Minor inconsistency in naming',
    2: 'Institutions vague or unnamed where specificity expected',
    3: 'Major identity mismatch or institutions appear fictitious',
  },
  education: {
    0: 'Education details specific and internally consistent',
    1: 'Minor missing details but plausible',
    2: 'Education dates directly contradict work timeline',
    3: 'Education claims internally impossible (e.g. PhD in 1 year)',
  },
  publications: {
    0: 'No publications claimed, or publications described with specific titles/venues',
    1: 'Publications mentioned vaguely but plausible',
    2: 'Publication claims directly contradict resume timeline',
    3: 'Publication claims clearly impossible or fabricated',
  },
  employment: {
    0: 'Clean progression, overlaps of 1-3 months are fine',
    1: 'Overlaps of 3-6 months, explainable by contract/transition',
    2: 'Multiple concurrent full-time roles overlapping significantly, unexplained',
    3: 'Physically impossible timeline (e.g. simultaneous roles on different continents)',
  },
  awards: {
    0: 'No awards or awards are specific and plausible',
    1: 'Award seems slightly out of place for career stage',
    2: 'Major award with no detail that contradicts career level',
    3: 'Prestigious award clearly fabricated for the career stage',
  },
  language: {
    0: 'Specific, field-appropriate language with concrete details',
    1: 'Some sections slightly generic',
    2: 'Buzzword-heavy with zero concrete details or metrics',
    3: 'Entire resume reads like AI-generated filler',
  },
};

function scoreColor(score: number) {
  if (score >= 80) return 'text-green-600 dark:text-green-400';
  if (score >= 60) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}
function scoreBarColor(score: number) {
  if (score >= 80) return 'bg-green-500';
  if (score >= 60) return 'bg-amber-500';
  return 'bg-red-500';
}
function scoreLabel(score: number) {
  if (score >= 80) return 'Low risk';
  if (score >= 60) return 'Medium risk';
  return 'High risk';
}

// ─── Collapsible ──────────────────────────────────────────────────────────────

function Collapsible({ label, children }: { label: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-gray-100 dark:border-gray-800">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <span>{label}</span>
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {open && children}
    </div>
  );
}

// ─── Fraud result body ────────────────────────────────────────────────────────

function FraudResult({ parsed }: { parsed: ParsedFraudResult }) {
  return (
    <div className="px-4 py-4 space-y-5">
      {/* Score row */}
      <div className="flex items-center gap-4">
        <div className="flex-shrink-0 text-center w-16">
          <div className={`text-4xl font-bold tabular-nums ${scoreColor(parsed.score)}`}>{parsed.score}</div>
          <div className="text-xs text-gray-400 mt-0.5">/ 100</div>
        </div>
        <div className="flex-1">
          <div className="flex justify-between mb-1.5">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Legitimacy score</span>
            <span className={`text-xs font-semibold ${scoreColor(parsed.score)}`}>{scoreLabel(parsed.score)}</span>
          </div>
          <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
            <div className={`h-full rounded-full ${scoreBarColor(parsed.score)}`} style={{ width: `${parsed.score}%` }} />
          </div>
        </div>
      </div>

      {/* Category scores */}
      <div>
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Category scores</p>
        <div className="space-y-2">
          {CATEGORIES.map(cat => {
            const val = parsed.categoryScores[cat.key] ?? 0;
            const rubricText = RUBRIC[cat.key]?.[val] ?? '';
            const color = val === 0 ? 'text-green-600 dark:text-green-400' : val === 1 ? 'text-amber-500' : 'text-red-500';
            const badgeBg = val === 0 ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : val === 1 ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
            return (
              <div key={cat.key} className={`rounded-lg border px-3 py-2.5 ${val === 0 ? 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/30' : badgeBg}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">{cat.label}</span>
                    <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">{Math.round(cat.weight * 100)}%</span>
                  </div>
                  <span className={`text-sm font-bold flex-shrink-0 ${color}`}>{val}/3</span>
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{rubricText}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Reasoning */}
      {parsed.reasoning && (
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Reasoning</p>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{parsed.reasoning}</p>
        </div>
      )}

      {/* Red flags */}
      <div>
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
          Red flags <span className="ml-1">({parsed.redFlags.length})</span>
        </p>
        {parsed.redFlags.length === 0
          ? <p className="text-sm text-green-600 dark:text-green-400">None found</p>
          : (
            <ul className="space-y-1.5">
              {parsed.redFlags.map((flag, i) => (
                <li key={i} className="flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{flag}</span>
                </li>
              ))}
            </ul>
          )}
      </div>
    </div>
  );
}

// ─── Result card ──────────────────────────────────────────────────────────────

function ResultCard({ r }: { r: TalentResult }) {
  return (
    <div className={`rounded-xl overflow-hidden border ${r.error ? 'border-red-200 dark:border-red-800' : 'border-gray-200 dark:border-gray-800'} bg-white dark:bg-gray-900 flex flex-col`}>
      {/* Card header */}
      <div className={`px-4 py-3 flex items-center justify-between border-b flex-shrink-0 ${r.error ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-800'}`}>
        <div className="flex items-center gap-2 min-w-0">
          {r.error ? <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" /> : <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />}
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{r.name}</span>
          <span className="text-xs text-gray-400 flex-shrink-0">#{r.talentId}</span>
        </div>
        {r.latencyMs !== undefined && (
          <span className="text-xs text-gray-400 flex-shrink-0 ml-2">{r.latencyMs}ms</span>
        )}
      </div>

      {/* Meta strip: model, tokens */}
      {(r.model || r.tokenUsage) && (
        <div className="flex items-center gap-4 px-4 py-2 bg-gray-50/50 dark:bg-gray-800/20 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
          {r.model && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              <span className="font-medium">Model</span> {r.model}
            </span>
          )}
          {r.tokenUsage && (
            <>
              <span className="text-xs text-gray-300 dark:text-gray-700">|</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                <span className="font-medium">Prompt</span> {r.tokenUsage.promptTokens.toLocaleString()} tok
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                <span className="font-medium">Completion</span> {r.tokenUsage.completionTokens.toLocaleString()} tok
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                <span className="font-medium">Total</span> {r.tokenUsage.totalTokens.toLocaleString()} tok
              </span>
            </>
          )}
        </div>
      )}

      {/* Error */}
      {r.error && <div className="px-4 py-4"><p className="text-sm text-red-600 dark:text-red-400">{r.error}</p></div>}

      {/* Fraud result */}
      {r.parsed && <div className="overflow-auto flex-1"><FraudResult parsed={r.parsed} /></div>}

      {/* Collapsibles */}
      {r.variables && (
        <Collapsible label="Variables used (today · profileText)">
          <div className="px-4 pb-3 pt-1 space-y-3 bg-gray-50 dark:bg-gray-800/30">
            <div>
              <span className="text-xs font-mono font-medium text-purple-600 dark:text-purple-400">{'{{today}}'}</span>
              <p className="mt-0.5 text-xs font-mono text-gray-700 dark:text-gray-300">{r.variables.today}</p>
            </div>
            <div>
              <span className="text-xs font-mono font-medium text-purple-600 dark:text-purple-400">{'{{profileText}}'}</span>
              <pre className="mt-0.5 text-xs font-mono text-gray-700 dark:text-gray-300 whitespace-pre-wrap max-h-48 overflow-auto">{r.variables.profileText}</pre>
            </div>
          </div>
        </Collapsible>
      )}
      {r.resolvedUserMessage && (
        <Collapsible label="Full user message sent">
          <pre className="px-4 pb-4 pt-1 text-xs text-gray-600 dark:text-gray-400 overflow-auto max-h-64 whitespace-pre-wrap bg-gray-50 dark:bg-gray-800/30">{r.resolvedUserMessage}</pre>
        </Collapsible>
      )}
      {r.rawResponse && (
        <Collapsible label="Raw LLM response">
          <pre className="px-4 pb-4 pt-1 text-xs text-gray-600 dark:text-gray-400 overflow-auto max-h-64 whitespace-pre-wrap bg-gray-50 dark:bg-gray-800/30">{r.rawResponse}</pre>
        </Collapsible>
      )}
    </div>
  );
}

// ─── Carousel ─────────────────────────────────────────────────────────────────

function ResultsCarousel({ results }: { results: TalentResult[] }) {
  const [idx, setIdx] = useState(0);

  // reset when results change
  useEffect(() => setIdx(0), [results]);

  if (results.length === 0) return null;

  const current = results[idx];

  return (
    <div className="flex flex-col gap-3">
      {/* Nav bar */}
      {results.length > 1 && (
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setIdx(i => Math.max(0, i - 1))}
            disabled={idx === 0}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Prev
          </button>

          {/* Dots */}
          <div className="flex items-center gap-1.5">
            {results.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIdx(i)}
                className={`rounded-full transition-all ${i === idx ? 'w-4 h-2 bg-blue-600' : 'w-2 h-2 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400'}`}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={() => setIdx(i => Math.min(results.length - 1, i + 1))}
            disabled={idx === results.length - 1}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Next <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <ResultCard r={current} />
    </div>
  );
}

// ─── Main Section ─────────────────────────────────────────────────────────────

export function PromptLabSection() {
  const [defaults, setDefaults] = useState<{ systemPrompt: string; userMessageTemplate: string } | null>(null);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [userMessageTemplate, setUserMessageTemplate] = useState('');
  const [talentIdsRaw, setTalentIdsRaw] = useState('');
  const [model, setModel] = useState(MODELS[0].value);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<TalentResult[]>([]);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    getPromptLabDefaults()
      .then(d => {
        setDefaults(d);
        setSystemPrompt(d.systemPrompt);
        setUserMessageTemplate(d.userMessageTemplate);
      })
      .catch(() => setFetchError('Failed to load defaults'))
      .finally(() => setLoading(false));
  }, []);

  const parsedIds = talentIdsRaw.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
  const canRun = !running && parsedIds.length > 0 && parsedIds.length <= 10 && systemPrompt.trim().length > 0;

  const handleReset = () => {
    if (!defaults) return;
    setSystemPrompt(defaults.systemPrompt);
    setUserMessageTemplate(defaults.userMessageTemplate);
    setResults([]);
  };

  const handleRun = async () => {
    if (!canRun) return;
    setRunning(true);
    setResults([]);
    try {
      const res = await testFraudPrompt({ systemPrompt, userMessageTemplate, talentIds: parsedIds, model });
      setResults(res);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err instanceof Error ? err.message : 'Request failed');
      setResults([{ talentId: '—', name: '—', error: msg }]);
    } finally {
      setRunning(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-32">
      <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
    </div>
  );

  if (fetchError) return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4">
        <p className="text-sm text-red-700 dark:text-red-300">{fetchError}</p>
      </div>
    </div>
  );

  return (
    <div className="p-8 max-w-[1400px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Prompt Lab</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Experiment with the local resume fraud analysis prompt against real talent data.</p>
        </div>
        <button
          type="button"
          onClick={handleReset}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Reset to defaults
        </button>
      </div>

      {/* Side-by-side */}
      <div className="grid grid-cols-2 gap-6 items-start">

        {/* LEFT — inputs */}
        <div className="space-y-4">
          {/* Talent IDs */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Talent IDs <span className="ml-1 text-gray-400 font-normal text-xs">one per line or comma-separated · max 10</span>
            </label>
            <textarea
              value={talentIdsRaw}
              onChange={e => setTalentIdsRaw(e.target.value)}
              rows={3}
              placeholder={'123456\n789012'}
              className="w-full px-3 py-2 text-sm font-mono border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
            />
            {parsedIds.length > 10 && <p className="mt-1 text-xs text-red-500">Max 10 IDs ({parsedIds.length} entered)</p>}
          </div>

          {/* Model */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Model</label>
            <AdminSelect
              value={model}
              onChange={setModel}
              options={MODELS.map(m => ({ value: m.value, label: m.label }))}
            />
          </div>

          {/* System Prompt */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">System Prompt</label>
            <textarea
              value={systemPrompt}
              onChange={e => setSystemPrompt(e.target.value)}
              rows={14}
              className="w-full px-3 py-2 text-sm font-mono border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
            />
          </div>

          {/* User Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              User Message
              <span className="ml-1.5 text-gray-400 font-normal text-xs">
                use <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{'{{profileText}}'}</code> and <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{'{{today}}'}</code>
              </span>
            </label>
            <textarea
              value={userMessageTemplate}
              onChange={e => setUserMessageTemplate(e.target.value)}
              rows={10}
              className="w-full px-3 py-2 text-sm font-mono border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
            />
          </div>

          {/* Run */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleRun}
              disabled={!canRun}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              {running ? 'Running…' : parsedIds.length > 1 ? `Run (${parsedIds.length})` : 'Run'}
            </button>
            {parsedIds.length === 0 && <span className="text-xs text-gray-400 dark:text-gray-500">Enter talent IDs to run</span>}
          </div>
        </div>

        {/* RIGHT — results carousel */}
        <div>
          {running && results.length === 0 && (
            <div className="flex items-center justify-center py-24 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          )}
          {!running && results.length === 0 && (
            <div className="flex items-center justify-center py-24 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-400 dark:text-gray-500">Results will appear here</p>
            </div>
          )}
          {results.length > 0 && <ResultsCarousel results={results} />}
        </div>
      </div>
    </div>
  );
}
